import Immutable from 'seamless-immutable';

import {
  FACET_URL_PARAMS,
  FACET_URL_PATH,
  PAGE_TYPE_CHANGE,
  RECEIVE_SEARCH_RESPONSE,
  REQUEST_SEARCH,
  RESET_FACET_GROUP,
  TOGGLE_FACETS,
  TOGGLE_SELECTED_FACET,
  TOGGLE_SELECTED_FACET_GROUP
} from 'constants/reduxActions';
import { SINGLE_SELECT_FILTERS } from 'constants/appConstants';
import { FILTERS_RE } from 'common/regex';
import marketplace from 'cfg/marketplace.json';

const {
  search: { topBrands }
} = marketplace;

const initialState = {
  requestedUrl: null,
  navigationV2: [],
  facetUrlQueryParams: null,
  facetUrlPath: null
};

export function isFacetsVisible(state = false, action) {
  const { type, visible, pageType } = action;
  switch (type) {
    case TOGGLE_FACETS:
      return visible;
    case PAGE_TYPE_CHANGE:
      return pageType === 'search' ? state : false;
    default:
      return state;
  }
}

export const sortTopBrands = (facets, orderedBrands = topBrands) => {
  const facetValuesCopy = [...facets.values];
  const foundTopValues = facetValuesCopy
    .filter((v, i) => {
      const foundIndex = orderedBrands.indexOf(v.name.toLowerCase()) > -1;
      if (foundIndex) {
        facetValuesCopy.splice(i, 1);
        return foundIndex;
      }
    })
    .sort((a, b) => orderedBrands.indexOf(a.name) - orderedBrands.indexOf(b.name));
  return foundTopValues.concat(facetValuesCopy);
};

export default function facetsV2(state = initialState, action) {
  const { type, isFresh, selectedFacetGroupIndex, selectedFacetGroupName, selectedFacetIndex, response, url, facetUrlPath, facetUrlQueryParams } =
    action;
  let newState = Immutable(state);

  switch (type) {
    case TOGGLE_SELECTED_FACET_GROUP: {
      const facetIndex = newState.navigationV2.findIndex(facet => facet.facetField === selectedFacetGroupName);

      if (facetIndex > -1) {
        newState = newState.updateIn(['navigationV2', facetIndex, 'isExpanded'], v => !v);
      }

      return newState;
    }

    case TOGGLE_SELECTED_FACET: {
      const facet = newState.navigationV2[selectedFacetGroupIndex];

      if (facet) {
        if (SINGLE_SELECT_FILTERS[facet.facetField]) {
          // Single-select facets: remove the group from the tree after selection
          newState = newState.updateIn(['navigationV2'], items => items.filter((item, i) => i !== selectedFacetGroupIndex));
        } else {
          // Multi-select facets: toggle the selected state
          newState = newState.updateIn(['navigationV2', selectedFacetGroupIndex, 'values', selectedFacetIndex, 'selected'], v => !v);
        }
      }

      return newState;
    }

    case REQUEST_SEARCH:
      if (isFresh) {
        newState = Immutable(initialState);
      }
      return newState.set('requestedUrl', url);

    case RECEIVE_SEARCH_RESPONSE:
      if (response.url === state.requestedUrl && response.navigationV2) {
        // Process navigationV2 flat array structure
        const navigationV2WithState = response.navigationV2.map(facetGroup => {
          const newFacetGroup = { ...facetGroup };
          const oldFacetGroup = state.navigationV2?.find(({ facetField }) => facetField === facetGroup.facetField);

          // Preserve UI state from previous render
          newFacetGroup.isExpanded = oldFacetGroup ? oldFacetGroup.isExpanded : false;

          // Process facet values
          if (newFacetGroup.values) {
            newFacetGroup.values = newFacetGroup.values.map(facetValue => {
              const newValue = { ...facetValue };

              // Handle facetUrl vs facetZsoUrl
              if (FILTERS_RE.test(facetValue.facetUrl)) {
                newValue.facetZsoUrl = facetValue.facetUrl;
                newValue.facetUrl = null;
              }

              // Add displayName if missing
              if (!facetValue.displayName) {
                newValue.displayName = facetValue.name;
              }

              return newValue;
            });

            // Apply top brands sorting for brand facets
            if (topBrands.length > 0 && facetGroup.facetField === 'brandNameFacet') {
              newFacetGroup.values = sortTopBrands({ values: newFacetGroup.values });
            }
          }

          return newFacetGroup;
        });

        newState = newState.set('navigationV2', navigationV2WithState);
      }

      return newState;

    case FACET_URL_PATH:
      newState = newState.set('facetUrlPath', facetUrlPath);
      return newState;

    case FACET_URL_PARAMS:
      newState = newState.set('facetUrlQueryParams', facetUrlQueryParams);
      return newState;

    case RESET_FACET_GROUP:
      newState.navigationV2.forEach((facetGroup, i) => {
        if (facetGroup.values) {
          facetGroup.values.forEach((facetValue, ii) => {
            if (facetValue.selected) {
              newState = newState.setIn(['navigationV2', i, 'values', ii, 'selected'], false);
            }
          });
        }
      });

      return newState;

    default:
      return state;
  }
}
